import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, PenSquare, AlertTriangle } from 'lucide-react';
import { MAIL_API, mailAuth, PANEL } from './mailUtils';
import { MailThreadList } from './MailThreadList';
import { MailComposer } from './MailComposer';
import { MailAlertsToggle } from './MailAlertsToggle';

export default function MailWidget() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compose, setCompose] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const s = await axios.get(`${MAIL_API}/status`, mailAuth(true));
        if (!alive) return;
        setStatus(s.data);
        if (s.data.ok) {
          const r = await axios.get(`${MAIL_API}/threads`, { ...mailAuth(true), params: { folder: 'inbox', q: 'is:unread', limit: 8 } });
          if (alive) setThreads(r.data.threads);
        }
      } catch (e) {
        if (!alive) return;
        if (e.response?.status === 403) setStatus({ ok: false, allowed: false });
        else setTimeout(() => alive && load(), 4000); // request aborted / network blip — try again shortly
      }
      if (alive) setLoading(false);
    };
    load();
    const iv = setInterval(load, 90000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (!status || !status.allowed) return null;

  return (
    <div className={`${PANEL} flex flex-col overflow-hidden`} data-testid="mail-widget">
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-sky-100 border border-sky-200 flex items-center justify-center relative"><Mail className="w-4 h-4 text-sky-700" />
          {status.unread > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" data-testid="mail-widget-new-dot" />}</div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-black text-slate-900">Unread</h3>
          <p className="text-[11px] text-slate-500 truncate" data-testid="mail-widget-count">{status.ok ? `${status.unread} unread in your inbox` : status.message}</p>
        </div>
        <button onClick={() => setCompose(true)} disabled={!status.can_send} className="p-2 rounded-lg hover:bg-slate-900/5 text-slate-500 hover:text-slate-900 disabled:opacity-40" title="New message" data-testid="mail-widget-compose"><PenSquare className="w-4 h-4" /></button>
        <button onClick={() => navigate('/mail')} className="px-2.5 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-xs text-white font-semibold flex items-center gap-1 shadow-sm" data-testid="mail-widget-open">Inbox <ArrowRight className="w-3 h-3" /></button>
      </div>
      <div className="px-4 pb-2"><MailAlertsToggle compact /></div>
      {!status.ok && <p className="px-4 pb-3 text-[11px] text-amber-700 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{status.action}</p>}
      {status.ok && (
        <div className="max-h-[360px] overflow-y-auto border-t border-slate-200/60">
          <MailThreadList threads={threads} loading={loading} compact onSelect={t => navigate(`/mail?folder=inbox&thread=${t.id}`)} emptyText="Inbox zero — nothing unread" />
        </div>
      )}
      {compose && <MailComposer draft={{ mode: 'new' }} onClose={() => setCompose(false)} />}
    </div>
  );
}
